import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  increment,
} from "firebase/firestore";

import imageCompression from "browser-image-compression";

import { db, auth } from "../firebase";

const getUserUsername = async (uid) => {
  const userSnap = await getDoc(
    doc(db, "publicUsers", uid)
  );

  if (userSnap.exists()) {
    return userSnap.data().username || "User";
  }

  return "User";
};

export async function publishRenovationListing({
  listing,
  user,
  submitting,
  setSubmitting,
  navigate,
  t,
}) {
  if (submitting) return;

  if (!user) {
    alert("You must be logged in to post a listing");
    return;
  }

  // ===============================
  // VALIDATION
  // ===============================

  if (!listing.title?.trim()) {
    alert("Product name is required");
    return;
  }

  if (!listing.price || Number(listing.price) <= 0) {
    alert("Valid price is required");
    return;
  }

  if (!listing.description || listing.description.trim().length < 10) {
    alert(t("listing.descriptionRequired"));
    return;
  }

  if (!listing.photos || listing.photos.length === 0) {
    alert(t("listing.photoRequired"));
    return;
  }

  try {
    const now = Date.now();

    // ===============================
    // USER PLAN LIMITS
    // ===============================

    const userRef = doc(
      db,
      "users",
      auth.currentUser.uid
    );

    const snap = await getDoc(userRef);
    const data = snap.data() || {};

    const plan = data.plan || "free";

    const trialEndAt = Number(
      data.trialEndAt || 0
    );

    const isTrialActive =
      plan === "gift_trial" &&
      now < trialEndAt;

    let limit = 3;

    if (isTrialActive) {
      limit = 9999;
    }

    const limitlessUntil = Number(
      data.limitlessUntil || 0
    );

    if (
      plan === "limitless" &&
      now < limitlessUntil
    ) {
      limit = 999999;
    }

    const MONTH_MS =
      30 * 24 * 60 * 60 * 1000;

    let count = data.monthlyPostCount ?? 0;

    let resetAt = data.monthlyPostResetAt;

    if (!resetAt || now >= resetAt) {
      count = 0;

      resetAt = now + MONTH_MS;

      await updateDoc(userRef, {
        monthlyPostCount: 0,
        monthlyPostResetAt: resetAt,
      });
    }

    setSubmitting(true);

    // ===============================
    // UPLOAD IMAGES
    // ===============================

    let uploadedPhotoUrls = listing.photos
      .filter(
        (p) =>
          typeof p === "string" || p?.url
      )
      .map((p) =>
        typeof p === "string"
          ? p
          : p.url
      );

    const hasNewFiles = listing.photos?.some(
      (p) => p.file
    );

    if (hasNewFiles) {
      const formData = new FormData();

      const compressedFiles =
        await Promise.all(
          listing.photos.map(async (photoObj) => {
            if (!photoObj.file) return null;

            try {
              return await imageCompression(
                photoObj.file,
                {
                  maxSizeMB: 1,
                  maxWidthOrHeight: 1200,
                  useWebWorker: true,
                }
              );
            } catch {
              return photoObj.file;
            }
          })
        );

      compressedFiles
        .filter(Boolean)
        .forEach((file) => {
          formData.append("files", file);
        });

      const res = await fetch(
        "https://vipart-backend.onrender.com/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      const uploadData = await res.json();

      console.log(
        "RENOVATION UPLOAD RESPONSE:",
        uploadData
      );

      if (!res.ok) {
        throw new Error("Upload failed");
      }

      uploadedPhotoUrls = [
        ...uploadedPhotoUrls,
        ...(uploadData.urls || []),
      ];
    }

    const safePhotos =
      uploadedPhotoUrls.filter(Boolean);

    // ===============================
    // USER
    // ===============================

    const firebaseUser = auth.currentUser;

    if (!firebaseUser?.uid) {
      throw new Error(
        "User not authenticated properly"
      );
    }

    const usernameFromDb =
      await getUserUsername(
        firebaseUser.uid
      );

    // ===============================
    // CREATE RENOVATION LISTING
    // ===============================

    const newListing = {
      ...listing,

      // IMPORTANT:
      // identifies this as renovation
      marketplaceType: "renovation",

      userId: firebaseUser.uid,

      username: usernameFromDb,

      accountType:
        user?.accountType || "regular",

      createdAtUser:
        user?.createdAt || "",

      title:
        listing.title || "",

      brand:
        listing.brand || "",

      condition:
        listing.condition || "",

      price:
        Number(listing.price),

      quantity:
        listing.quantity === "" ||
        listing.quantity == null
          ? null
          : Number(listing.quantity),

      unit:
        listing.unit || "",

      deliveryAvailable:
        Boolean(listing.deliveryAvailable),

      warranty:
        Boolean(listing.warranty),

      description:
        listing.description || "",

      photos: safePhotos,

      // Same publishing flow as real estate
      status: "draft",

      createdAt:
        new Date().toISOString(),
    };

    console.log(
      "RENOVATION LISTING TO FIRESTORE:",
      newListing
    );

    // ===============================
    // FREE / TRIAL LIMIT
    // ===============================

    if (count >= limit) {
      const docRef = await addDoc(
        collection(db, "listings"),
        {
          ...newListing,
          status: "draft",
        }
      );

      console.log(
        "RENOVATION DRAFT CREATED:",
        docRef.id
      );

      navigate("/trial-ended", {
        state: {
          listingId: docRef.id,
        },
      });

      return;
    }

    // ===============================
    // CREATE LISTING
    // ===============================

    const docRef = await addDoc(
      collection(db, "listings"),
      {
        ...newListing,
        status: "draft",
      }
    );

    console.log(
      "RENOVATION FIRESTORE DOCUMENT CREATED:",
      docRef.id
    );

    // ===============================
    // INCREMENT MONTHLY POST COUNT
    // ===============================

    await updateDoc(userRef, {
      monthlyPostCount:
        increment(1),

      monthlyPostResetAt:
        resetAt,
    });

    // ===============================
    // GO TO UPGRADE
    // ===============================

    navigate("/listing-upgrade", {
      replace: true,
      state: {
        listingId: docRef.id,
      },
    });

  } catch (error) {
    console.error(
      "publishRenovationListing error:",
      error
    );

    alert(
      error.message ||
      "Something went wrong"
    );
  } finally {
    setSubmitting(false);
  }
}
